import React, { useRef, useContext, useState, useEffect } from "react";
import {
  PostPurchaseContext,
  PostPurchaseDispatchContext,
} from "../../context/PostPurchaseContext";
import {
  registerSerialNumber,
  numDaysBetween,
  getSerialNumberData,
  getProductData,
} from "../../utilities";
import ExtendWarranty from "../ExtendWarranty";
import FormDateInput from "../FormDateInput";
import FormFileInput from "../FormFileInput";
import FormSelect from "../FormSelect";
import FormTextInput from "../FormTextInput";
import NextButton from "../NextButton";
import BackButton from "../BackButton";
import Popup from "../Popup";

function Step2() {
  const state = useContext(PostPurchaseContext);
  const dispatch = useContext(PostPurchaseDispatchContext);

  const [canExtendWarranty, setCanExtendWarranty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});
  const [showModelNumberPopup, setShowModelNumberPopup] = useState(false);
  const [showSerialNumberPopup, setShowSerialNumberPopup] = useState(false);

  const modelNumberRef = useRef(null);
  const serialNumberRef = useRef(null);
  const purchaseDateRef = useRef(null);
  const purchasePlaceRef = useRef(null);

  const requiredInputRefs = [
    modelNumberRef,
    serialNumberRef,
    purchaseDateRef,
    purchasePlaceRef,
  ];

  useEffect(() => {
    if (within30Days()) {
      setCanExtendWarranty(true);
    } else {
      setCanExtendWarranty(false);
    }
  }, [state]);

  function within30Days() {
    if (!state.purchaseDate) return false;
    const purchaseDate = new Date(state.purchaseDate);
    const today = new Date();
    const timePassed = numDaysBetween(purchaseDate, today);
    return timePassed <= 30;
  }

  async function handleGoToNextStep() {
    setLoading(true);
    // Send a fetch request to the app with the serial number to get back the sku
    const { sku } = await getSerialNumberData(state.serialNumber)

    // Check the sku matches the model number input by the customer
    if (sku !== state.modelNumber) {
      // Handle error
      console.error("Serial Number doesn't match model number")
      return;
    }

    const productData = await getProductData(sku);
    if (!productData) {
      // Handle error
      console.error("Product not found");
      return;
    }
    // Store the product data in state
    dispatch({
      type: "setCustomerData",
      payload: { name: "productName", value: productData.title },
    });
    dispatch({
      type: "setCustomerData",
      payload: { name: "kitData", value: productData.compatible_products.kit },
    });
    dispatch({
      type: "setCustomerData",
      payload: {
        name: "filtersData",
        value: productData.compatible_products.filters,
      },
    });

    // Register the serial number to the customer
    await registerSerialNumber(productData.title, state);
    setLoading(false);
    
    dispatch({ type: "setActiveStep", payload: state.activeStep + 1 });
  }

  return (
    <div className="flex flex-col items-start text-start gap-6 py-8">
      <div className="flex flex-col gap-6 w-full lg:flex-row">
        <FormTextInput
          errorMessage="Enter your model number."
          ref={modelNumberRef}
          label="Model Number"
          name="modelNumber"
          extraClasses="before:top-[31%]"
          moreInfo={{ text: "Where do I find my model number?",  onclick: () => setShowModelNumberPopup(true) }}
        />
        {showModelNumberPopup && (
          <Popup 
            extraClasses="bg-[color:var(--backgroundColor)]"
            popupTitle={'Where to Find Your Model Number:'}
            message={'Find the Brio logo sticker on your product. The model number is located below the Brio logo.'}
            imageSrc={'https://cdn.shopify.com/s/files/1/0560/8144/9004/files/Brio-care-model-number.png?v=1681389748'}
            onClose={() => setShowModelNumberPopup(false)}
          />
        )}
        <FormTextInput
          errorMessage="Enter your serial number"
          ref={serialNumberRef}
          label="Serial Number"
          name="serialNumber"
          extraClasses="before:top-[31%]"
          moreInfo={{ text: "Where do I find my serial number?", onclick: () => setShowSerialNumberPopup(true) }}
        />
        {showSerialNumberPopup && (
          <Popup 
            extraClasses="bg-[color:var(--backgroundColor)]"
            popupTitle={'Where to Find Your Serial Number:'}
            message={'Find the bar code sticker on your product. The serial number is the 10-digit code located below the bar code.'}
            imageSrc={'https://cdn.shopify.com/s/files/1/0560/8144/9004/files/Brio-car-serial-number.png?v=1681389747'}
            onClose={() => setShowSerialNumberPopup(false)}
          />
        )}
      </div>

      <div className="flex flex-col gap-6 w-full lg:flex-row">
        <FormDateInput
          name="purchaseDate"
          errorMessage="Enter your purchase date."
          label="Purchase Date"
          ref={purchaseDateRef}
        />
        <FormSelect
          label="Place of Purchase"
          name="purchasePlace"
          errorMessage="Enter your place of purchase"
          ref={purchasePlaceRef}
          options={[
            { value: "briowater", label: "briowater.com" },
            { value: "amazon", label: "Amazon.com" },
            { value: "walmart", label: "Walmart.com" },
            { value: "target", label: "Target.com" },
            { value: "wayfair", label: "Wayfair.com" },
            { value: "homedepot", label: "Homedepot.com" },
            { value: "other", label: "Other" },
          ]}
        />
      </div>
      <div className="px-2 text-[14px]">
        Proof of purchase will be required for all{" "}
        <a
          href="https://briowater.com/policies/refund-policy"
          className="text-brio-700 hover:text-brio-700"
        >
          warranty claims
        </a>
        . You have the option of providing proof of purchase now by uploading a
        photo or PDF of your sales receipt.
      </div>
      <FormFileInput />
      <div class="button-wrapper">
      <BackButton isLoading={false} text="Back" />
      <NextButton
        requiredInputRefs={requiredInputRefs}
        beforeDismount={handleGoToNextStep}
        text="Next"
        isLoading={loading}
      />
      </div>
    </div>
  );
}

export default Step2;
